import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Single source of truth for the `hiring_goal_id` URL search param.
 *
 * The opening-details page lets a user pin the view to a past hiring goal via
 * the URL (`?hiring_goal_id=...`). Several siblings need to read it (so their
 * fetches don't wait on the funnel API) and a few need to either set it or
 * carry it across navigation.
 *
 * Centralising it here means the literal `'hiring_goal_id'` live in exactly one place.
 *
 * - `hiringGoalId` — current value, `undefined` when unset (i.e. current goal).
 * - `setHiringGoalId(id)` — set/replace the param; pass `undefined` to clear.
 * - `withHiringGoalId(search)` — build a `search` string for `<Link to>` /
 *   `navigate(...)` that preserves the current `hiring_goal_id` on top of an
 *   existing `URLSearchParams` (or any `URLSearchParams` ctor input).
 */
const HIRING_GOAL_ID_PARAM = 'hiring_goal_id';

export function useHiringGoalIdParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const hiringGoalId = searchParams.get(HIRING_GOAL_ID_PARAM) ?? undefined;

  const setHiringGoalId = useCallback(
    (id: string | undefined) =>
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (id) next.set(HIRING_GOAL_ID_PARAM, id);
        else next.delete(HIRING_GOAL_ID_PARAM);
        return next;
      }),
    [setSearchParams],
  );

  const withHiringGoalId = useCallback(
    (search?: ConstructorParameters<typeof URLSearchParams>[0]) => {
      const next = new URLSearchParams(search);
      if (hiringGoalId) next.set(HIRING_GOAL_ID_PARAM, hiringGoalId);
      else next.delete(HIRING_GOAL_ID_PARAM);
      return next.toString();
    },
    [hiringGoalId],
  );

  return { hiringGoalId, setHiringGoalId, withHiringGoalId };
}
